import { Component, HostListener, Input, OnDestroy, OnInit } from '@angular/core';
import { AudioService } from '../../serwisy/audio.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'mm-audio-player',
  templateUrl: './audio-player.component.html',
  styleUrls: ['./audio-player.component.scss']
})
export class AudioPlayerComponent implements OnInit, OnDestroy {

  @Input() src!: string;
  isLoading = true;
  private audioCtx = new AudioContext();
  private buffer: AudioBuffer | null = null;
  private sourceNode: AudioBufferSourceNode | null = null;
  private gainNode = this.audioCtx.createGain();

  isPlaying = false;
  duration = 0;
  currentTime = 0;

  private startedAt = 0;
  private pausedAt = 0;
  private rafId: number | null = null;

  isSeeking = false;
  private seekTimeout: any;

  showOptions = false;

  showVolume = false;
  volume = 1;
  lastVolume = 1;
  isMuted = false;

  speed = 1;
  showSpeed = false;
  speedOptions = [
    0.75,
    1,
    1.25,
  ];

  private sub?: Subscription;
  constructor(private audioService: AudioService) { }
  ngOnInit() {
    this.loadAudio();
  }

  ngOnDestroy(): void {
    if (this.sourceNode) {
      try {
        this.sourceNode.stop();
      } catch { }
      this.sourceNode.disconnect();
      this.sourceNode = null;
    }

    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }

  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;

    if (target.closest('.volume')) {
      return;
    }

    if (target.closest('.options')) {
      return;
    }

    if (this.showVolume) {
      this.showVolume = false;
    }

    if (this.showOptions) {
      this.showOptions = false;
    }
    if (target.closest('.options')) {
      return;
    }

    this.showSpeed = false;
    this.showOptions = false;
    this.showVolume = false;
  }


  loadAudio() {
    this.isLoading = true;

    this.sub = this.audioService.getAudioBuffer(this.src).subscribe({
      next: (buffer) => {
        this.buffer = buffer;
        this.duration = buffer.duration;

        this.audioCtx = this.audioService.getAudioContext();

        this.gainNode = this.audioCtx.createGain();
        this.gainNode.gain.value = this.volume;
        this.gainNode.connect(this.audioCtx.destination);
      },
      error: () => {
        this.isLoading = false;
      },
      complete: () => {
        this.isLoading = false;
      }
    });
  }
  play() {
    if (!this.buffer) return;
  this.audioCtx = this.audioService.getAudioContext();
    this.sourceNode = this.audioCtx.createBufferSource();
    this.sourceNode.buffer = this.buffer;
    this.sourceNode.playbackRate.value = this.speed;

    this.sourceNode.connect(this.gainNode);

    const offset = this.pausedAt;
    this.startedAt = this.audioCtx.currentTime - offset;

    this.sourceNode.start(0, offset);
    this.isPlaying = true;

    this.trackProgress();
  }

  pause() {
    if (!this.sourceNode) return;

    this.sourceNode.stop();
    this.pausedAt = this.audioCtx.currentTime - this.startedAt;
    this.isPlaying = false;

    if (this.rafId) cancelAnimationFrame(this.rafId);
  }


  seekTo(value: number) {
    if (!this.buffer) return;

    const newTime = Number(value);

    clearTimeout(this.seekTimeout);

    this.seekTimeout = setTimeout(() => {
      this.isSeeking = true;
    }, 120)


    this.pausedAt = newTime;

    if (this.isPlaying) {
      if (this.sourceNode) {
        try {
          this.sourceNode.stop();
        } catch { }
      }

      this.sourceNode = this.audioCtx.createBufferSource();
      this.sourceNode.buffer = this.buffer;
      this.sourceNode.playbackRate.value = this.speed;
      this.sourceNode.connect(this.gainNode);

      this.startedAt = this.audioCtx.currentTime - newTime;
      this.sourceNode.start(0, newTime);

      this.currentTime = newTime;
    } else {
      this.currentTime = newTime;
    }

    requestAnimationFrame(() => {
      clearTimeout(this.seekTimeout);
      this.isSeeking = false;
    });

  }

  private trackProgress() {
    const update = () => {
      if (!this.isPlaying) return;

      const t = this.audioCtx.currentTime - this.startedAt;
      this.currentTime = t;

      if (t < this.duration) {
        this.rafId = requestAnimationFrame(update);
      } else {
        this.isPlaying = false;
      }
    };

    update();
  }

  downloadFile() {
    const a = document.createElement('a');
    a.href = this.src;
    a.download = this.src.split('/').pop() || 'audio.mp3';
    a.click();
  }

  setSpeed(speed: number) {
    this.speed = Number(speed);

    if (this.sourceNode) {
      this.sourceNode.playbackRate.value = this.speed;
    }
  }

  onVolumeIconClick() {
    if (!this.showVolume) {
      this.showVolume = true;
      return;
    }

    this.toggleMute();
  }

  setVolume(v: number) {
    this.volume = Number(v);

    if (this.volume <= 0) {
      this.volume = 0;
      this.isMuted = true;
      this.gainNode.gain.value = 0;
      return;
    }

    this.isMuted = false;
    this.lastVolume = this.volume;
    this.gainNode.gain.value = this.volume;
  }

  toggleMute() {
    if (this.isMuted || this.volume === 0) {
      this.isMuted = false;
      this.volume = this.lastVolume || 1;
      this.gainNode.gain.value = this.volume;
    } else {
      this.isMuted = true;
      this.lastVolume = this.volume || 1;
      this.volume = 0;
      this.gainNode.gain.value = 0;
    }
  }
  get currentTimeRounded(): number {
    return Math.floor(this.currentTime);
  }

  private formatTime(totalSeconds: number): string {
    const sec = Math.floor(totalSeconds);

    if (sec < 60) {
      return `0:${sec
        .toString()
        .padStart(2, '0')}`;
    }

    const hours = Math.floor(sec / 3600);
    const minutes = Math.floor((sec % 3600) / 60);
    const seconds = sec % 60;

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds
        .toString()
        .padStart(2, '0')}`;
    }

    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }

  get currentTimeHuman(): string {
    return this.formatTime(this.currentTime);
  }

  get durationHuman(): string {
    return this.formatTime(this.duration);
  }

}
